<template>
  <div :class="$style.wrapper" :style="wrapperCustomStyles">
    <AlertMessage v-if="showErrorMessage" type="error">
      {{ displayedErrorMessage }}
    </AlertMessage>
    <VeeForm
      class="c-form"
      v-slot="{ values, meta, validate }"
      :validation-schema="schema"
      :initial-values="initialValues"
      data-cy="form"
      @submit="onSubmit"
    >
      <div class="c-form__row">
        <InputTextGroup name="firstName" label="First Name" />
      </div>
      <div class="c-form__row">
        <InputTextGroup name="lastName" label="Last Name" />
      </div>
      <div class="c-form__row">
        <FormPhoneInput
          :dropdown-attrs="{
            name: 'countryCallingCode',
            options: countryCallingCodes,
            optionLabel: 'nameString',
            placeholder: 'Select',
            filter: true,
          }"
          :input-attrs="{
            name: 'phoneNumber',
            label: 'Phone',
          }"
        />
      </div>
      <div class="c-form__row">
        <Checkbox
          name="agreedToKyc"
          :model-value="values.agreedToKyc"
          @input="
            async (value) => {
              values.agreedToKyc = value;
              await validate(); // Validate the form after checkbox change
            }
          "
        >
          The information you provide may be shared with third parties to verify
          your identity, comply with law and prevent fraud. You consent to its
          collection, access, use and disclosure per our
          <a
            href="https://clear.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            >Privacy Policy</a
          >.
        </Checkbox>
      </div>
      <div class="c-form__controls">
        <DSButton
          label="Next"
          class="m-auto"
          :class="$style.button"
          type="submit"
          :loading="isLoading"
          :disabled="!meta.valid"
          data-cy="next-button"
        />
      </div>
    </VeeForm>
  </div>
</template>

<script>
import { CONTACT_SUPPORT_MSG } from '@/data/error-messages';
import { mapGetters } from 'vuex';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { RouteNames } from '@/data/routes';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import { parsePhoneNumber } from 'libphonenumber-js';
import { yup } from '@clearbanc/validation';
import { Form as VeeForm } from 'vee-validate';
import { getAllCallingCodes } from '@/utils/phone-numbers';

import { useProfiling } from '@/composables/useProfiling';
import { EVENT_TYPES } from '@/data/lexisnexis-types';

import AlertMessage from '@/components/banners/AlertMessage.vue';
import DSButton from 'primevue/button';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import FormPhoneInput from '@/components/forms/FormPhoneInput.vue';
import Checkbox from '@/components/Checkbox.vue';

export default {
  components: {
    AlertMessage,
    VeeForm,
    DSButton,
    InputTextGroup,
    FormPhoneInput,
    Checkbox,
  },
  data() {
    return {
      showErrorMessage: false,
      errorMessage: null,
      countryCallingCodes: getAllCallingCodes(),
      initialValues: {},
      isLoading: false,
      schema: yup.object({
        firstName: yup.userName(),
        lastName: yup.userName(),
        phoneNumber: yup.phone('countryCallingCode'),
        agreedToKyc: yup
          .boolean()
          .oneOf([true], 'You must agree to the Identity Verification Policy.'),
      }),
    };
  },
  setup() {
    const profiling = useProfiling(RouteNames.ONBOARDING.USER, {
      type: EVENT_TYPES.ACCOUNT_CREATION,
    });

    return { profiling };
  },
  computed: {
    displayedErrorMessage() {
      return (
        this.errorMessage || CONTACT_SUPPORT_MSG(this.contactDetails.number)
      );
    },
    ...mapGetters([
      'user',
      'paymentsOnboardingSteps',
      'contactDetails',
      'isEmailVerified',
    ]),
    ...mapRequestStatuses({
      updateUserRequest: 'UPDATE_USER',
    }),
    currentRoute() {
      return this.$route.name;
    },
    wrapperCustomStyles() {
      const marginTop = this.isEmailVerified ? '35px' : undefined;
      return { marginTop };
    },
  },
  beforeMount() {
    this.updateInitialValues();
  },
  methods: {
    parsePhoneNumber,
    updateInitialValues() {
      if (this.user?.firstName) {
        this.initialValues.firstName = this.user.firstName;
      }

      if (this.user?.lastName) {
        this.initialValues.lastName = this.user.lastName;
      }

      if (this.user?.phoneNumber) {
        const parsedPhoneNumber = this.parsePhoneNumber(
          this.user.phoneNumber,
          this.user.addressCountryCode || COUNTRY_CODES.US,
        );
        const callingCode = this.countryCallingCodes.find((item) => {
          return item.value === parsedPhoneNumber.country;
        });

        this.initialValues.countryCallingCode = callingCode;
        this.initialValues.phoneNumber = parsedPhoneNumber.nationalNumber;
      } else {
        this.initialValues.countryCallingCode = this.countryCallingCodes.find(
          (item) => {
            return item.value === 'US';
          },
        );
      }

      this.initialValues.agreedToKyc = this.user?.agreedToKyc ?? false;
    },
    async onSubmit(values) {
      this.isLoading = true;

      const formValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: `+${values.countryCallingCode?.callingCode}${values.phoneNumber}`,
        agreedToKyc: values.agreedToKyc,
      };

      await this.$store.dispatchApiAction('UPDATE_USER', formValues);

      if (this.updateUserRequest.isError) {
        this.showErrorMessage = true;
        const errorMessage = this.updateUserRequest.errorMessage;
        analytics.track(SEGMENT_EVENTS.clear_pay_error, {
          errorMessage,
        });
      } else {
        analytics.track(SEGMENT_EVENTS.SUBMITTED_BASIC_PROFILE);
        this.$store.dispatch(
          'UPDATE_PAYMENTS_ONBOARDING_STEP',
          this.currentRoute,
        );

        this.profiling.finish();

        this.$router.push({
          name: this.paymentsOnboardingSteps[this.currentRoute].nextRoute.name,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>

<style lang="less" module>
.wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.button {
  min-width: 186px;
}
</style>
